import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { PlanningData } from '../../types/onboarding';
import { analyzePlanning, generateBannersBatch } from '../../services/api/onboardingService';

interface PlanningVisualizationStepProps {
  serviceUrl: string;
  onComplete: (planningData: PlanningData, leadId: number, batchSessionId?: string) => void;
  onBack: () => void;
}

// Planning step definitions
interface PlanningStepItem {
  label: string;
  activeLabel: string;
  icon: string;
}

const PLANNING_STEPS: PlanningStepItem[] = [
  { label: '商品情報の分析が完了しました', activeLabel: '商品情報の分析中...', icon: 'search' },
  { label: '競合情報の収集が完了しました', activeLabel: '競合情報の収集中...', icon: 'chart' },
  { label: 'ブランド情報の整理が完了しました', activeLabel: 'ブランド情報の整理中...', icon: 'palette' },
  { label: 'キーコピーの抽出が完了しました', activeLabel: 'キーコピーの抽出中...', icon: 'text' },
];

const STEP_INTERVAL_MS = 1750;
const MIN_DURATION_MS = 7000;

// --- Icons ---

const SearchIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const ChartIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);

const PaletteIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="13.5" cy="6.5" r="2.5" />
    <circle cx="17.5" cy="10.5" r="2.5" />
    <circle cx="8.5" cy="7.5" r="2.5" />
    <circle cx="6.5" cy="12.5" r="2.5" />
    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" />
  </svg>
);

const TextIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 6H3" />
    <path d="M21 12H3" />
    <path d="M15.5 18H3" />
  </svg>
);

const CheckIcon: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const renderIcon = (iconType: string, color: string) => {
  switch (iconType) {
    case 'search': return <SearchIcon color={color} />;
    case 'chart': return <ChartIcon color={color} />;
    case 'palette': return <PaletteIcon color={color} />;
    case 'text': return <TextIcon color={color} />;
    default: return null;
  }
};

// --- Tailwind class constants (旧 styled-components) ---
// 旧 keyframes `pulse` / `fadeIn` / `progressAnimation` は globals.css の
// `obs-pulse-fade` / `obs-fade-in-left` / `obs-progress-sweep` へ移設。
// 旧 keyframes `spin`（1s linear infinite）は Tailwind 標準の `animate-spin` と同内容。

const CONTAINER = 'w-full flex flex-col items-center py-8';

const TITLE = 'text-[1.375rem] leading-[1.4] font-bold text-gray-800 mb-2 text-center';

const SUBTITLE = 'text-[0.875rem] text-gray-500 mb-8 text-center';

const SPINNER_CONTAINER = 'w-16 h-16 mb-8';

const SPINNER =
  'w-16 h-16 border-4 border-gray-200 border-t-4 border-t-violet-600 rounded-full animate-spin';

const STEPS_CONTAINER = 'w-full max-w-[400px] mb-6';

const STEP_ROW_VISIBLE = 'flex items-center py-3 animate-[obs-fade-in-left_0.4s_ease-out]';
const STEP_ROW_HIDDEN = 'hidden items-center py-3 animate-[obs-fade-in-left_0.4s_ease-out]';

const STEP_ICON: Record<'pending' | 'active' | 'completed', string> = {
  completed:
    'w-8 h-8 rounded-full flex items-center justify-center shrink-0 mr-3.5 ' +
    'transition-all duration-300 ease-[ease] bg-violet-600',
  active:
    'w-8 h-8 rounded-full flex items-center justify-center shrink-0 mr-3.5 ' +
    'transition-all duration-300 ease-[ease] bg-violet-100 ' +
    'animate-[obs-pulse-fade_1.5s_ease-in-out_infinite]',
  pending:
    'w-8 h-8 rounded-full flex items-center justify-center shrink-0 mr-3.5 ' +
    'transition-all duration-300 ease-[ease] bg-gray-100',
};

const STEP_TEXT: Record<'pending' | 'active' | 'completed', string> = {
  completed: 'text-[0.9rem] text-gray-700 font-normal',
  active: 'text-[0.9rem] text-violet-600 font-medium',
  pending: 'text-[0.9rem] text-gray-400 font-normal',
};

const PROGRESS_BAR_CONTAINER =
  'w-full max-w-[400px] h-1 bg-gray-200 rounded-[2px] overflow-hidden mt-2';

// width は連続値なので style={{ width }} でインライン指定。
// ::after のスイープは percent < 100 のときだけ（離散）なのでクラス文字列を出し分ける。
const PROGRESS_BAR_FILL =
  'h-full bg-[linear-gradient(90deg,#7c3aed,#a78bfa)] rounded-[2px] ' +
  'transition-[width] duration-500 ease-[ease]';

const PROGRESS_BAR_FILL_SWEEPING =
  'h-full bg-[linear-gradient(90deg,#7c3aed,#a78bfa)] rounded-[2px] ' +
  'transition-[width] duration-500 ease-[ease] ' +
  "after:content-[''] after:block after:w-full after:h-full " +
  'after:bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.3),transparent)] ' +
  'after:animate-[obs-progress-sweep_1.5s_ease-in-out_infinite]';

const BACK_BUTTON =
  'bg-transparent bg-none border-0 text-gray-400 text-[0.8125rem] cursor-pointer ' +
  'mt-6 py-2 px-4 transition-[color] duration-200 ease-[ease] hover:text-gray-500';

// 解析失敗時のエラーパネル（無言停止を防ぐ）
const ERROR_BOX = 'flex flex-col items-center gap-3 py-8 px-6 text-gray-500';

const ERROR_TITLE = 'text-[0.9375rem] font-semibold text-red-700 m-0';

const ERROR_TEXT = 'text-[0.8125rem] leading-[1.6] text-gray-500 m-0 text-center';

const RETRY_BUTTON =
  'mt-1 py-2.5 px-6 text-[0.9375rem] font-semibold text-white bg-violet-600 border-0 ' +
  'rounded-lg cursor-pointer transition-[background-color] duration-200 ease-[ease] ' +
  'hover:bg-violet-700';

export const PlanningVisualizationStep: React.FC<PlanningVisualizationStepProps> = ({
  serviceUrl,
  onComplete,
  onBack,
}) => {
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [apiResult, setApiResult] = useState<{
    planning: PlanningData;
    leadId: number;
  } | null>(null);
  const [apiDone, setApiDone] = useState(false);
  const [apiError, setApiError] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  const [bannerTriggered, setBannerTriggered] = useState(false);
  const [batchSessionId, setBatchSessionId] = useState<string | undefined>(undefined);
  const startTimeRef = useRef(Date.now());
  const hasCalledCompleteRef = useRef(false);
  const mountedRef = useRef(true);
  const onCompleteRef = useRef(onComplete);

  // Keep onComplete ref up to date
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Track mount state
  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  // API call: analyze planning
  useEffect(() => {
    let cancelled = false;
    setApiError(false);

    analyzePlanning(serviceUrl).then((response) => {
      if (!cancelled && mountedRef.current) {
        setApiResult({
          planning: response.planning,
          leadId: response.lead_id,
        });
        setApiError(false);
        setApiDone(true);
      }
    }).catch(() => {
      if (!cancelled && mountedRef.current) {
        // 失敗時は無言で止めず、エラーUIを出して再試行/戻るへ誘導する
        setApiError(true);
        setApiDone(true);
      }
    });

    return () => { cancelled = true; };
  }, [serviceUrl, retryNonce]);

  // 解析の再試行（無限「分析中」を解消する導線）
  const handleRetry = useCallback(() => {
    setApiError(false);
    setApiDone(false);
    setApiResult(null);
    hasCalledCompleteRef.current = false;
    setRetryNonce((n) => n + 1);
  }, []);

  // Trigger banner generation in background when step 3 starts completing
  useEffect(() => {
    if (activeStepIndex >= 2 && apiResult && !bannerTriggered) {
      setBannerTriggered(true);
      // Fire and forget - banner generation runs in background
      generateBannersBatch({
        lead_id: apiResult.leadId,
        planning: apiResult.planning,
        analysis: {
          logo_url: apiResult.planning.brand_info.logo_url,
          og_image_url: apiResult.planning.brand_info.og_image_url,
          favicon_url: null,
          colors: apiResult.planning.brand_info.colors,
          title: apiResult.planning.product_features.title,
          description: '',
          category: apiResult.planning.competitor_info.industry,
          domain: null,
          logo_text_fallback: null,
        },
      }).then((response) => {
        if (mountedRef.current) {
          setBatchSessionId(response.session_id);
        }
      }).catch(() => {
        // Non-blocking: banner generation failure handled later
      });
    }
  }, [activeStepIndex, apiResult, bannerTriggered]);

  // Step animation: advance through steps sequentially
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStepIndex(prev => {
        if (prev < PLANNING_STEPS.length - 1) {
          setCompletedSteps(s => new Set(s).add(prev));
          return prev + 1;
        }
        // Mark last step as completed
        setCompletedSteps(s => new Set(s).add(prev));
        clearInterval(interval);
        return prev;
      });
    }, STEP_INTERVAL_MS);

    return () => clearInterval(interval);
  }, []);

  // Transition to next step when both animation and API are done
  const tryComplete = useCallback(() => {
    if (hasCalledCompleteRef.current) return;

    const allStepsCompleted = completedSteps.size === PLANNING_STEPS.length;
    const elapsed = Date.now() - startTimeRef.current;
    const minDurationMet = elapsed >= MIN_DURATION_MS;

    if (allStepsCompleted && apiDone && minDurationMet && apiResult) {
      hasCalledCompleteRef.current = true;
      setTimeout(() => {
        if (mountedRef.current) {
          onCompleteRef.current(apiResult.planning, apiResult.leadId, batchSessionId);
        }
      }, 500);
    }
  }, [completedSteps, apiDone, apiResult, batchSessionId]);

  useEffect(() => {
    tryComplete();
  }, [tryComplete]);

  // Schedule completion check if min duration not yet met
  useEffect(() => {
    if (completedSteps.size === PLANNING_STEPS.length && apiDone && apiResult && !hasCalledCompleteRef.current) {
      const elapsed = Date.now() - startTimeRef.current;
      const remaining = MIN_DURATION_MS - elapsed;
      if (remaining > 0) {
        const timer = setTimeout(tryComplete, remaining + 100);
        return () => clearTimeout(timer);
      }
    }
  }, [completedSteps, apiDone, apiResult, tryComplete]);

  const getStepStatus = (index: number): 'pending' | 'active' | 'completed' => {
    if (completedSteps.has(index)) return 'completed';
    if (index === activeStepIndex) return 'active';
    return 'pending';
  };

  const progressPercent = (completedSteps.size / PLANNING_STEPS.length) * 100;

  if (apiError) {
    return (
      <div className={CONTAINER}>
        <h2 className={TITLE}>分析に失敗しました</h2>
        <div className={ERROR_BOX}>
          <p className={ERROR_TITLE}>URLの分析を完了できませんでした</p>
          <p className={ERROR_TEXT}>
            サイトの構造や通信状況により取得できないことがあります。
            <br />
            もう一度お試しいただくか、別のURLでお試しください。
          </p>
          <button className={RETRY_BUTTON} type="button" onClick={handleRetry}>
            再試行する
          </button>
          <button className={BACK_BUTTON} type="button" onClick={onBack}>
            別のURLを入力する
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={CONTAINER}>
      <h2 className={TITLE}>広告プランニングを<br />AIが作成しています...</h2>
      <p className={SUBTITLE}>URLから最適な広告戦略を分析中です</p>

      <div className={SPINNER_CONTAINER}>
        <div className={SPINNER} />
      </div>

      <div className={STEPS_CONTAINER}>
        {PLANNING_STEPS.map((step, index) => {
          const status = getStepStatus(index);
          const isVisible = index <= activeStepIndex;
          const iconColor = status === 'completed' ? '#ffffff' :
            status === 'active' ? '#7c3aed' : '#9ca3af';

          return (
            <div key={index} className={isVisible ? STEP_ROW_VISIBLE : STEP_ROW_HIDDEN}>
              <div className={STEP_ICON[status]}>
                {status === 'completed' ? <CheckIcon /> : renderIcon(step.icon, iconColor)}
              </div>
              <span className={STEP_TEXT[status]}>
                {status === 'completed' ? step.label : step.activeLabel}
              </span>
            </div>
          );
        })}
      </div>

      <div className={PROGRESS_BAR_CONTAINER}>
        <div
          className={progressPercent < 100 ? PROGRESS_BAR_FILL_SWEEPING : PROGRESS_BAR_FILL}
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      <button className={BACK_BUTTON} type="button" onClick={onBack}>
        戻る
      </button>
    </div>
  );
};

export default PlanningVisualizationStep;
